import { useElementSize } from '@helpers';
import clsx from 'clsx';
import { type ChangeEvent, type ReactNode, useRef } from 'react';

import { Tooltip } from '../tooltip/Tooltip';
import styles from './Slider.module.scss';

const THUMB_SIZE = 20;

export interface SliderProps {
  value: number;
  min: number;
  max: number;
  step: number;
  onValueChange: (value: number) => void;
  'aria-label': string;
  valueLabel: string;
  startLabel?: ReactNode;
  endLabel?: ReactNode;
  className?: string;
  'data-testid'?: string;
}

export const Slider = ({
  value,
  min,
  max,
  step,
  onValueChange,
  'aria-label': ariaLabel,
  valueLabel,
  startLabel,
  endLabel,
  className,
  'data-testid': dataTestId,
}: SliderProps) => {
  const controlRef = useRef<HTMLDivElement>(null);
  const { width: controlWidth } = useElementSize(controlRef);

  // Ranges are expected to divide evenly by step (a native range input can't
  // reach a max that doesn't), so marks sit at even splits between the ends.
  const intervalCount =
    step > 0 && max > min ? Math.round((max - min) / step) : 0;
  const intermediateMarks = Array.from(
    { length: Math.max(0, intervalCount - 1) },
    (_, index) => ((index + 1) / intervalCount) * 100
  );
  const position =
    max > min
      ? Math.min(100, Math.max(0, ((value - min) / (max - min)) * 100))
      : 0;
  const tooltipOffsetX =
    controlWidth > 0
      ? (position / 100 - 0.5) * Math.max(0, controlWidth - THUMB_SIZE)
      : 0;

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    onValueChange(event.currentTarget.valueAsNumber);
  };

  return (
    <div className={clsx(styles.root, className)}>
      {!!startLabel && <span className={styles.label}>{startLabel}</span>}
      <div className={styles.control} ref={controlRef} data-control>
        <span className={styles.track} aria-hidden="true">
          <span className={styles.marks}>
            {intermediateMarks.map(markPosition => (
              <span
                key={markPosition}
                className={styles.mark}
                style={{ left: `${markPosition}%` }}
                data-slider-mark
              />
            ))}
          </span>
        </span>
        <Tooltip
          dismissible={false}
          hasDelay={false}
          hasMaxWidth={false}
          inPortal
          label={valueLabel}
          offsetX={tooltipOffsetX}
        >
          <input
            className={styles.input}
            type="range"
            value={value}
            min={min}
            max={max}
            step={step}
            aria-label={ariaLabel}
            aria-valuetext={valueLabel}
            data-testid={dataTestId}
            onChange={handleChange}
          />
        </Tooltip>
      </div>
      {!!endLabel && <span className={styles.label}>{endLabel}</span>}
    </div>
  );
};
